"use client";

import React, { forwardRef, useEffect, useMemo, useRef } from 'react';
import FormInputDropdown from '../input-dropdown';

const DEFAULT_TIME_ID = '09:30';
const DEFAULT_TIME_LABEL = '09 : 30 AM';

/**
 * Time picker input — wraps FormInputDropdown with 30-minute interval options
 * and a clock icon. Styled per Figma: 318px max-width, 50px height, 5px radius.
 * Defaults to 09:30 AM.
 *
 * @param {Object} props
 * @param {String} props.name Form input name
 * @param {String} [props.label] Label text (default: "Lab draw time")
 * @param {Function} [props.onChange] Fires on selection change
 * @param {Boolean} [props.required] Required flag
 * @param {Boolean} [props.disabled] Disabled flag
 * @param {String} [props.customClass] Additional class names
 * @param {*} ref
 */
function FormInputTimePicker(
  {
    name,
    label = 'Lab draw time',
    onChange,
    required = false,
    disabled = false,
    customClass,
    ...rest
  },
  ref
) {
  const dropdownRef = useRef(null);

  const timeOptions = useMemo(() => {
    const options = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const period = h < 12 ? 'AM' : 'PM';
        const minutes = String(m).padStart(2, '0');
        const timeLabel = `${String(hour12).padStart(2, '0')} : ${minutes} ${period}`;
        const timeId = `${String(h).padStart(2, '0')}:${minutes}`;
        options.push({ key: options.length, id: timeId, label: timeLabel, labelText: timeLabel, code: timeId });
      }
    }
    return options;
  }, []);

  const wrapperRef = useRef(null);

  const wrapperClass = `form-input-time-picker${customClass ? ` ${customClass}` : ''}`;

  // Pre-select 09:30 AM on mount
  useEffect(() => {
    const defaultIndex = timeOptions.findIndex(o => o.id === DEFAULT_TIME_ID);
    if (defaultIndex !== -1 && dropdownRef.current) {
      dropdownRef.current.setIndexValue(defaultIndex);
    }
  }, [timeOptions]);

  // Scroll to the selected item when the dropdown opens
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const observer = new MutationObserver(() => {
      const list = wrapper.querySelector('.form-input-dropdown__select-list--active');
      if (!list) return;
      const selected = list.querySelector('label.selected');
      if (selected) {
        const listHeight = list.clientHeight;
        const itemTop = selected.closest('li').offsetTop;
        const itemHeight = selected.closest('li').offsetHeight;
        list.scrollTo({ top: itemTop - listHeight / 2 + itemHeight / 2, behavior: 'instant' });
      }
    });

    observer.observe(wrapper, { attributes: true, subtree: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} className={wrapperClass}>
      <FormInputDropdown
        ref={dropdownRef}
        name={name}
        label={label}
        placeholder={DEFAULT_TIME_LABEL}
        itemList={timeOptions}
        onChange={onChange}
        required={required}
        disabled={disabled}
        disableFirstItem={false}
        omitFirstItem
        omitBlurValidate={!required}
        enableCustomScrollbar={200}
        customClass="form-input-time-picker__dropdown"
        {...rest}
      />
      <span className="form-input-time-picker__clock-icon" aria-hidden="true">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2ZM12 4C9.87827 4 7.84344 4.84285 6.34315 6.34315C4.84285 7.84344 4 9.87827 4 12C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12C20 9.87827 19.1571 7.84344 17.6569 6.34315C16.1566 4.84285 14.1217 4 12 4ZM12 6C12.2449 6.00003 12.4813 6.08996 12.6644 6.25272C12.8474 6.41547 12.9643 6.63975 12.993 6.883L13 7V11.586L15.707 14.293C15.8863 14.473 15.9905 14.7144 15.9982 14.9684C16.006 15.2223 15.9168 15.4697 15.7488 15.6603C15.5807 15.8508 15.3464 15.9703 15.0935 15.9944C14.8406 16.0185 14.588 15.9454 14.387 15.79L14.293 15.707L11.293 12.707C11.1376 12.5514 11.0378 12.349 11.009 12.131L11 12V7C11 6.73478 11.1054 6.48043 11.2929 6.29289C11.4804 6.10536 11.7348 6 12 6Z" fill="#C4C4C4"/>
        </svg>
      </span>
    </div>
  );
}

export default forwardRef(FormInputTimePicker);
